import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import { PRODUCTS, ProductItem } from "../data/productsData";
import { loadProducts } from "../services/productSource";
import { loadSection } from "../services/contentSource";
import pageFallback from "../data/caseStudiesPage.json";
import "../styles/revamp.css";

/**
 * Case studies.
 *
 * These are built from the problem/solution content already written for each
 * product, not from customer outcomes. That distinction is deliberate and
 * visible in the copy: nothing here names a client, quotes a person, or claims
 * a result that a customer achieved, because no such material was available.
 *
 * When a real study exists — a named client, an agreed quote, a measured result
 * they have approved in writing — it should replace the corresponding entry
 * here rather than sit alongside it. Until then this page answers "what does
 * this look like in practice" honestly, which is a weaker claim than a case
 * study but a true one.
 */

const CaseStudies: React.FC = () => {
    const [products, setProducts] = useState<ProductItem[]>(PRODUCTS);
    const [page, setPage] = useState<any>(pageFallback);

    useEffect(() => {
        let cancelled = false;
        loadProducts().then((next) => {
            if (!cancelled) setProducts(next);
        });
        loadSection("caseStudiesPage", pageFallback as any).then(({ data: main }) => {
            if (!cancelled && main) setPage(main);
        });
        return () => {
            cancelled = true;
        };
    }, []);

    return (
        <>
            <Seo
                title="Case studies"
                description="How MindSyncX systems are applied in practice, across hospitality, B2B SaaS, healthcare and corporate facilities."
                path="/case-studies"
            />
            <main className="mx-catalog">
                <div className="container">
                    <p className="mx-eyebrow">{page.eyebrow}</p>
                    <h1 className="mx-suite-title">{page.title}</h1>
                    <p className="mx-catalog-lede">
                        {page.lede}
                    </p>

                    <div className="mx-catalog-grid">
                        {products.map((p) => (
                            <article
                                className="mx-card"
                                key={p.slug}
                                style={{ ["--mx-dot-color" as any]: p.accent }}
                            >
                                <p className="mx-eyebrow">
                                    <span className="mx-dot" /> {p.category}
                                </p>
                                <h2 className="mx-card-title">{p.name}</h2>

                                <div>
                                    <p className="mx-eyebrow" style={{ marginBottom: 6 }}>Before</p>
                                    <p className="mx-card-body">{p.problem}</p>
                                </div>

                                <div>
                                    <p className="mx-eyebrow" style={{ marginBottom: 6 }}>After</p>
                                    <p className="mx-card-body">{p.solution}</p>
                                </div>

                                <ul className="mx-card-stack">
                                    {p.metrics.map((m) => (
                                        <li key={m.label}>
                                            <span className="mx-metric-value">{m.value}</span> {m.label}
                                        </li>
                                    ))}
                                </ul>

                                <div className="mx-card-actions">
                                    <Link className="mx-btn mx-btn-primary" to={`/products/${p.slug}`}>
                                        Read the detail
                                    </Link>
                                </div>
                            </article>
                        ))}
                    </div>

                    <section className="mx-detail-cta" style={{ marginTop: 48 }}>
                        <h2 className="mx-card-title">{page.ctaTitle}</h2>
                        <div className="mx-suite-actions">
                            <Link className="mx-btn mx-btn-primary" to="/contact">
                                {page.ctaButtonText}
                            </Link>
                        </div>
                    </section>
                </div>
            </main>
        </>
    );
};

export default CaseStudies;
